import React from 'react';
import {StatusContextValue, useContextStatus} from '../common/ContextStatus';
import {useOktaAuth} from '@okta/okta-react';
import {useDeviceContext} from "../TelematicsDevice/TelematicsDeviceProvider";
import {DeviceStateApi, OutputModeEnum, OutputPolarityEnum} from "../TelematicsDevice/DeviceStateApi";
import {useAccessibilityContext} from "../AccessibilityProvider/AccessibilityProvider";

type OutputModeContextValue = StatusContextValue & {
  enabled: boolean;
  setEnabled: (enabled: boolean) => void;
};

const OutputModeContext = React.createContext<
  OutputModeContextValue | undefined
>(undefined);

type OutputModeProviderProps = {
  children: React.ReactNode;
};

const OutputModeProvider = (props: OutputModeProviderProps) => {
  const [enabled, setEnabled] = React.useState<boolean>(false);

  const [contextStatus, setContextStatus] = React.useState({
    loading: false,
    saving: false,
    hasContent: false
  });

  const value: OutputModeContextValue = React.useMemo(
    () => ({
      enabled,
      setEnabled,
      contextStatus,
      setContextStatus
    }),
    [enabled,contextStatus]
  );
  return <OutputModeContext.Provider value={value} {...props} />;
};

const useOutputModeContext = () => {
  const context = React.useContext(OutputModeContext);
  const { authState } = useOktaAuth();
  const { identity} = useDeviceContext();
  const { accessibility } = useAccessibilityContext();

  if (!context) {
    throw new Error(
      'useOutputModeContext must be used within an OutputModeProvider'
    );
  }

  const status = useContextStatus(context);
  const { enabled, setEnabled, contextStatus } = context;

  const save = (newEnabled: boolean) => {
    if (!accessibility?.outputMode.visible) {
      return Promise.reject('User not allowed to toggle the output mode');
    }

    status.savingStarted();

    return DeviceStateApi.updateState(authState?.accessToken, identity.id,
      {
        io: {
          output1: {
            mode: newEnabled ? OutputModeEnum.HIGH : OutputModeEnum.LOW,
            period: "PERMANENT",
            polarity: OutputPolarityEnum.KEEP_CURRENT
          }
        }
      })
      .then(() => {
        setEnabled(newEnabled);
        status.succeed();
      })
      .catch(err => {
        status.failed(err.message, 'Could not update output mode');
      });
  };

  return {
    enabled,

    save,

    contextStatus,
    dismissError: status.dismissError,
    dismissSuccess: status.dismissSuccess
  };
};

export { OutputModeProvider, useOutputModeContext };
